import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { NgpSelectPortal } from 'ng-primitives/select';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  heroArrowLeft,
  heroArrowRight,
  heroCheck,
  heroChevronLeft,
  heroChevronRight,
  heroMagnifyingGlass,
  heroPlus,
  heroUserPlus,
} from '@ng-icons/heroicons/outline';
import { of, switchMap, type Observable } from 'rxjs';
import {
  ToastrService,
  UiBadge,
  UiButton,
  UiEmptyText,
  UiHint,
  UiInput,
  UiLabel,
  UiOptionRow,
  UiOptionRowCopy,
  UiOptionRowIcon,
  UiPageHeader,
  UiPanelCard,
  UiPanelText,
  UiPanelTitle,
  UiResourceCardSkeleton,
  UiResourceGrid,
  UiSelect,
  UiSelectDropdown,
  UiSelectOption,
  UiSkeletonGrid,
  UiStepNav,
  UiStepPill,
  UiStepPillBadge,
  UiStepPillLabel,
} from '../../../shared/ui';
import { RevenueApiService } from '../catalog/revenue-api.service';
import { ProductCardComponent } from '../catalog/product-card.component';
import { LeadsApiService, type ContactSearchResult } from '../leads/leads-api.service';
import { PRODUCT_TYPES, PRODUCT_TYPE_ICONS, productTypeIcon, productTypeLabel } from '../catalog/product-types.catalog';
import {
  PRODUCT_BILLING_INTERVALS,
  PRODUCT_CUSTOM_UNIT_OPTIONS,
  formatProductPrice,
  type ProductBillingInterval,
  type ProductCustomIntervalUnit,
} from '../catalog/product-billing.utils';
import type { RevenueProduct } from '../../../core/api/generated/models/RevenueProduct';
import type { CreateRevenueProductRequest } from '../../../core/api/generated/models/CreateRevenueProductRequest';
import { SalesApiService } from './sales-api.service';
import type { CreateSalePayload, SaleType } from './sales.types';

type WizardStep = 'lead' | 'product' | 'review';
type BadgeTone = 'success' | 'warning' | 'danger' | 'neutral' | 'accent';

interface NewContactDraft {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

interface SelectedContact {
  id: string;
  label: string;
  source: 'existing' | 'new';
  draft?: NewContactDraft;
}

interface ProductDraft {
  name: string;
  description: string;
  type: string | null;
  amount: string;
  currency: string;
  billingInterval: ProductBillingInterval;
  customValue: number | null;
  customUnit: ProductCustomIntervalUnit | null;
}

function emptyProductDraft(): ProductDraft {
  return {
    name: '',
    description: '',
    type: null,
    amount: '',
    currency: 'BRL',
    billingInterval: 'one_time',
    customValue: null,
    customUnit: null,
  };
}

const BILLING_LABEL_PT_BR: Record<string, string> = {
  one_time: 'Pagamento único',
  monthly: 'Mensal',
  quarterly: 'Trimestral',
  semiannual: 'Semestral',
  annual: 'Anual',
  custom: 'Personalizado',
};

@Component({
  selector: 'app-vendas-criar-page',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    NgIcon,
    NgpSelectPortal,
    UiBadge,
    UiButton,
    UiEmptyText,
    UiHint,
    UiInput,
    UiLabel,
    UiOptionRow,
    UiOptionRowCopy,
    UiOptionRowIcon,
    UiPageHeader,
    UiPanelCard,
    UiPanelText,
    UiPanelTitle,
    UiResourceCardSkeleton,
    UiResourceGrid,
    ProductCardComponent,
    UiSelect,
    UiSelectDropdown,
    UiSelectOption,
    UiSkeletonGrid,
    UiStepNav,
    UiStepPill,
    UiStepPillBadge,
    UiStepPillLabel,
  ],
  providers: [
    provideIcons({
      heroArrowLeft,
      heroArrowRight,
      heroCheck,
      heroChevronLeft,
      heroChevronRight,
      heroMagnifyingGlass,
      heroPlus,
      heroUserPlus,
      ...PRODUCT_TYPE_ICONS,
    }),
  ],
  template: `
    <section class="catalog-shell">
      <app-ui-page-header
        title="Nova venda"
        subtitle="Vincule um lead, escolha o produto e revise antes de criar."
        [divider]="false"
      >
        <div actions class="flex flex-wrap gap-2">
          <a appButton variant="ghost" routerLink="/vendas">
            <ng-icon name="heroArrowLeft" size="14" /> Cancelar
          </a>
        </div>
      </app-ui-page-header>

      <nav appUiStepNav aria-label="Passos da criação">
        @for (s of stepsMeta; track s.key; let i = $index) {
          <button
            appUiStepPill
            type="button"
            tabindex="-1"
            aria-disabled="true"
            [active]="step() === s.key"
            [complete]="stepIndex(s.key) < stepIndex(step())"
          >
            <span appUiStepPillBadge>{{ i + 1 }}</span>
            <span appUiStepPillLabel>{{ s.label }}</span>
          </button>
        }
      </nav>

      <!-- ── Step 1: Lead ── -->
      @if (step() === 'lead') {
        <section appUiPanelCard class="card-panel">
          <div class="card-panel__head">
            <span appUiLabel class="mb-1 block uppercase">Passo 1</span>
            <h2 appUiPanelTitle class="mb-0 text-[1.2rem]">Quem está comprando?</h2>
            <p appUiPanelText>Selecione um lead existente ou cadastre um novo contato.</p>
          </div>

          <label class="search-field">
            <ng-icon name="heroMagnifyingGlass" size="16" aria-hidden="true" />
            <input
              appInput
              surface="panel"
              type="search"
              placeholder="Buscar por nome ou e-mail…"
              [ngModel]="search()"
              (ngModelChange)="onSearchChange($event)"
              aria-label="Buscar contatos"
            />
          </label>

          @if (search().trim().length === 1) {
            <p appUiHint>Digite pelo menos 2 letras para buscar.</p>
          } @else if (loadingLeads()) {
            <div aria-busy="true" aria-label="Buscando contatos">
              <app-ui-skeleton-grid [rows]="3" [rowHeight]="42" />
            </div>
          } @else if (visibleContacts().length === 0) {
            <p appUiEmptyText>Nenhum contato encontrado. Cadastre um novo abaixo.</p>
          } @else {
            <ul class="lead-list" role="listbox" aria-label="Contatos">
              @for (c of visibleContacts(); track c.id) {
                <li>
                  <button
                    appUiOptionRow
                    compact
                    type="button"
                    class="lead-item"
                    [selected]="selectedContact()?.id === c.id"
                    [class.is-selected]="selectedContact()?.id === c.id"
                    (click)="pickExisting(c)"
                  >
                    <span class="lead-item__name">{{ contactName(c) }}</span>
                    <span appBadge [tone]="contactStatusTone(c.status)" size="xs" class="lead-item__status">
                      {{ contactStatusLabel(c.status) }}
                    </span>
                  </button>
                </li>
              }
            </ul>
          }

          <details appUiPanelCard class="new-contact" [open]="newContactOpen()">
            <summary (click)="$event.preventDefault(); toggleNewContact()">
              <ng-icon name="heroUserPlus" size="14" /> Cadastrar novo contato
            </summary>
            @if (newContactOpen()) {
              <div class="new-contact__grid">
                <label class="form-field">
                  <span appUiLabel class="uppercase">Nome</span>
                  <input
                    appInput
                    type="text"
                    [ngModel]="newContact().firstName"
                    (ngModelChange)="updateNewContact('firstName', $event)"
                    placeholder="João"
                  />
                </label>
                <label class="form-field">
                  <span appUiLabel class="uppercase">Sobrenome</span>
                  <input
                    appInput
                    type="text"
                    [ngModel]="newContact().lastName"
                    (ngModelChange)="updateNewContact('lastName', $event)"
                    placeholder="da Silva"
                  />
                </label>
                <label class="form-field">
                  <span appUiLabel class="uppercase">E-mail</span>
                  <input
                    appInput
                    type="email"
                    [ngModel]="newContact().email"
                    (ngModelChange)="updateNewContact('email', $event)"
                    placeholder="joao@empresa.com"
                  />
                </label>
                <label class="form-field">
                  <span appUiLabel class="uppercase">Telefone</span>
                  <input
                    appInput
                    type="tel"
                    [ngModel]="newContact().phone"
                    (ngModelChange)="updateNewContact('phone', $event)"
                    placeholder="+55 11 91234-5678"
                  />
                </label>
              </div>
              <p appUiHint>Informe pelo menos e-mail ou telefone para criar o lead.</p>
              <button
                appButton
                variant="secondary"
                type="button"
                (click)="useNewContact()"
                [disabled]="!canUseNewContact()"
              >
                Usar este contato
              </button>
            }
          </details>

          <div class="card-panel__actions">
            <span [appUiHint]="selectedContact() ? 'neutral' : 'warning'">
              @if (selectedContact()) {
                Selecionado: <strong>{{ selectedContact()!.label }}</strong>
              } @else {
                Nenhum contato selecionado.
              }
            </span>
            <button
              appButton
              variant="primary"
              type="button"
              [disabled]="!selectedContact()"
              (click)="goTo('product')"
            >
              Continuar <ng-icon name="heroArrowRight" size="14" />
            </button>
          </div>
        </section>
      }

      <!-- ── Step 2: Product ── -->
      @if (step() === 'product') {
        <section appUiPanelCard class="card-panel">
          <div class="card-panel__head">
            <span appUiLabel class="mb-1 block uppercase">Passo 2</span>
            <h2 appUiPanelTitle class="mb-0 text-[1.2rem]">Qual produto?</h2>
            <p appUiPanelText>Escolha um produto do catálogo ou cadastre direto daqui.</p>
          </div>

          @if (productCreateMode() === 'closed') {
            <div class="picker-toolbar">
              <label class="search-field picker-search">
                <ng-icon name="heroMagnifyingGlass" size="16" aria-hidden="true" />
                <input
                  appInput
                  surface="panel"
                  type="search"
                  placeholder="Buscar produto…"
                  [ngModel]="productSearch()"
                  (ngModelChange)="onProductSearchChange($event)"
                  aria-label="Buscar produto"
                />
              </label>

              <div class="picker-type">
                <div appSelect [ngpSelectValue]="productType() ?? '__all__'" (ngpSelectValueChange)="setProductType($event)" aria-label="Filtrar por tipo">
                  <span>
                    @if (productType() === '__none__') { Sem tipo } @else if (productType()) { {{ productTypeLabel(productType()) }} } @else { Todos os tipos }
                  </span>
                  <div *ngpSelectPortal appSelectDropdown>
                    <div appSelectOption [ngpSelectOptionValue]="'__all__'">Todos os tipos</div>
                    @for (t of productTypes; track t.key) {
                      <div appSelectOption [ngpSelectOptionValue]="t.key">{{ t.label }}</div>
                    }
                    <div appSelectOption [ngpSelectOptionValue]="'__none__'">Sem tipo</div>
                  </div>
                </div>
              </div>

              <button appButton variant="secondary" size="sm" type="button" (click)="openProductCreate()" class="picker-new">
                <ng-icon name="heroPlus" size="16" /> Novo produto
              </button>
            </div>

            @if (loadingProducts()) {
              <app-ui-resource-grid minColumnWidth="15rem" aria-label="Carregando produtos">
                @for (_ of [0, 1, 2, 3]; track $index) {
                  <app-ui-resource-card-skeleton layout="mediaText" minHeight="10.5rem" />
                }
              </app-ui-resource-grid>
            } @else if (products().length === 0) {
              <p appUiEmptyText>Nenhum produto encontrado. Ajuste a busca ou cadastre um novo.</p>
            } @else {
              <app-ui-resource-grid minColumnWidth="15rem" role="group" aria-label="Produtos disponíveis">
                @for (p of products(); track p.id) {
                  <app-product-card
                    [product]="p"
                    interactive
                    [selected]="selectedProductId() === p.id"
                    [disabled]="!isProductSellable(p)"
                    [attr.title]="isProductSellable(p) ? null : 'Configure preço no catálogo para usar este produto'"
                    (pressed)="setProduct(p.id)"
                  >
                    @if (!isProductSellable(p)) {
                      <span card-badges><span appBadge tone="warning" size="xs">Preço pendente</span></span>
                    }
                  </app-product-card>
                }
              </app-ui-resource-grid>

              @if (productTotalPages() > 1) {
                <nav class="picker-pagination" aria-label="Paginação de produtos">
                  <button appButton variant="ghost" size="sm" type="button" [disabled]="!productHasPrev() || loadingProducts()" (click)="prevProductPage()">
                    <ng-icon name="heroChevronLeft" size="14" /> Anterior
                  </button>
                  <span class="picker-pagination__page">Pág. {{ productPage() }} de {{ productTotalPages() }}</span>
                  <button appButton variant="ghost" size="sm" type="button" [disabled]="!productHasNext() || loadingProducts()" (click)="nextProductPage()">
                    Próxima <ng-icon name="heroChevronRight" size="14" />
                  </button>
                </nav>
              }

              @if (selectedProduct(); as prod) {
                <div appUiPanelCard class="product-snapshot">
                  <div class="snapshot-row">
                    <span appUiLabel>Valor unitário</span>
                    <span class="snapshot-value">{{ formatMoney(prod.amount, prod.currency) }}</span>
                  </div>
                  <div class="snapshot-row">
                    <span appUiLabel>Cobrança</span>
                    <span class="snapshot-value">{{ billingLabel(prod.billing_interval) }}</span>
                  </div>
                  <div class="snapshot-row">
                    <span appUiLabel>Tipo de venda</span>
                    <span class="snapshot-value">{{ derivedType() === 'recurring' ? 'Assinatura' : 'Avulsa' }}</span>
                  </div>
                </div>

                <div class="form-grid">
                  <label class="form-field">
                    <span appUiLabel class="uppercase">Quantidade</span>
                    <input
                      appInput
                      type="number"
                      min="1"
                      step="1"
                      [ngModel]="quantity()"
                      (ngModelChange)="setQuantity($event)"
                    />
                  </label>
                  <div class="form-field">
                    <span appUiLabel class="uppercase">Total estimado</span>
                    <p class="total-preview">{{ formatMoney(computedTotal(), prod.currency) }}</p>
                  </div>
                </div>
              }
            }
          }

          <!-- Inline product create panel: 2 steps (type → form) -->
          @if (productCreateMode() === 'type') {
            <div appUiPanelCard class="create-panel">
              <div class="create-panel__head">
                <div>
                  <span appUiLabel class="mb-1 block uppercase">Novo produto</span>
                  <h3 appUiPanelTitle class="mb-0 text-[1.05rem]">Que tipo de produto?</h3>
                  <p appUiPanelText>Escolha o que melhor descreve o que você vende.</p>
                </div>
                <button appButton variant="ghost" size="sm" type="button" (click)="closeProductCreate()">
                  Fechar
                </button>
              </div>
              <div class="type-grid">
                @for (t of productTypes; track t.key) {
                  <button type="button" appUiOptionRow (click)="selectProductType(t.key)">
                    <div appUiOptionRowIcon aria-hidden="true">
                      <ng-icon [name]="t.icon" size="20" />
                    </div>
                    <div appUiOptionRowCopy>
                      <strong>{{ t.label }}</strong>
                      <small>{{ t.description }}</small>
                    </div>
                  </button>
                }
              </div>
            </div>
          }

          @if (productCreateMode() === 'form') {
            <div appUiPanelCard class="create-panel">
              <div class="create-panel__head">
                <div>
                  <span appUiLabel class="mb-1 block uppercase">Novo {{ productTypeLabel(productDraft().type).toLowerCase() }}</span>
                  <h3 appUiPanelTitle class="mb-0 text-[1.05rem]">Detalhes do produto</h3>
                  <p appUiPanelText>Defina nome e preço — outros detalhes podem ser ajustados depois no catálogo.</p>
                </div>
                <button appButton variant="ghost" size="sm" type="button" (click)="backToTypeSelection()">
                  <ng-icon name="heroArrowLeft" size="14" /> Voltar
                </button>
              </div>

              <div class="create-form-grid">
                <label class="form-field form-field--span-2">
                  <span appUiLabel class="uppercase">Nome <span class="required">*</span></span>
                  <input
                    appInput
                    type="text"
                    [ngModel]="productDraft().name"
                    (ngModelChange)="updateProductDraft('name', $event)"
                    placeholder="Ex: Mentoria Premium"
                    [disabled]="productSubmitting()"
                  />
                </label>
                <label class="form-field form-field--span-2">
                  <span appUiLabel class="uppercase">Descrição (opcional)</span>
                  <input
                    appInput
                    type="text"
                    [ngModel]="productDraft().description"
                    (ngModelChange)="updateProductDraft('description', $event)"
                    placeholder="Resumo curto"
                    [disabled]="productSubmitting()"
                  />
                </label>
                <label class="form-field">
                  <span appUiLabel class="uppercase">Preço <span class="required">*</span></span>
                  <input
                    appInput
                    type="text"
                    inputmode="decimal"
                    [ngModel]="productDraft().amount"
                    (ngModelChange)="updateProductDraft('amount', $event)"
                    placeholder="0,00"
                    [disabled]="productSubmitting()"
                  />
                </label>
                <div class="form-field">
                  <span appUiLabel class="uppercase">Moeda</span>
                  <div
                    appSelect
                    [ngpSelectValue]="productDraft().currency"
                    (ngpSelectValueChange)="updateProductDraft('currency', $any($event))"
                  >
                    <span>{{ productDraft().currency }}</span>
                    <div *ngpSelectPortal appSelectDropdown>
                      <div appSelectOption [ngpSelectOptionValue]="'BRL'">BRL</div>
                      <div appSelectOption [ngpSelectOptionValue]="'USD'">USD</div>
                      <div appSelectOption [ngpSelectOptionValue]="'EUR'">EUR</div>
                    </div>
                  </div>
                </div>
                <div class="form-field form-field--span-2">
                  <span appUiLabel class="uppercase">Cobrança</span>
                  <div
                    appSelect
                    [ngpSelectValue]="productDraft().billingInterval"
                    (ngpSelectValueChange)="updateProductDraft('billingInterval', $any($event))"
                  >
                    <span>{{ billingLabel(productDraft().billingInterval) }}</span>
                    <div *ngpSelectPortal appSelectDropdown>
                      @for (b of billingOptions; track b.value) {
                        <div appSelectOption [ngpSelectOptionValue]="b.value">{{ b.label }}</div>
                      }
                    </div>
                  </div>
                </div>
                @if (productDraft().billingInterval === 'custom') {
                  <label class="form-field">
                    <span appUiLabel class="uppercase">Intervalo (valor)</span>
                    <input
                      appInput
                      type="number"
                      min="1"
                      step="1"
                      [ngModel]="productDraft().customValue"
                      (ngModelChange)="updateProductDraft('customValue', $event)"
                    />
                  </label>
                  <div class="form-field">
                    <span appUiLabel class="uppercase">Unidade</span>
                    <div
                      appSelect
                      [ngpSelectValue]="productDraft().customUnit"
                      (ngpSelectValueChange)="updateProductDraft('customUnit', $any($event))"
                    >
                      <span>{{ customUnitLabel(productDraft().customUnit) }}</span>
                      <div *ngpSelectPortal appSelectDropdown>
                        @for (u of customUnitOptions; track u.value) {
                          <div appSelectOption [ngpSelectOptionValue]="u.value">{{ u.label }}</div>
                        }
                      </div>
                    </div>
                  </div>
                }
              </div>

              @if (productSubmitError()) {
                <div appUiPanelCard class="error-banner" role="alert">{{ productSubmitError() }}</div>
              }

              <div class="create-panel__actions">
                <button appButton variant="ghost" type="button" (click)="closeProductCreate()" [disabled]="productSubmitting()">
                  Cancelar
                </button>
                <button
                  appButton
                  variant="primary"
                  type="button"
                  (click)="submitProductCreate()"
                  [disabled]="!canSubmitProduct() || productSubmitting()"
                >
                  <ng-icon name="heroCheck" size="14" />
                  {{ productSubmitting() ? 'Criando…' : 'Criar produto' }}
                </button>
              </div>
            </div>
          }

          @if (productCreateMode() === 'closed') {
            <div class="card-panel__actions">
              <button appButton variant="ghost" type="button" (click)="goTo('lead')">
                <ng-icon name="heroArrowLeft" size="14" /> Voltar
              </button>
              <button
                appButton
                variant="primary"
                type="button"
                [disabled]="!canContinueProduct()"
                (click)="goTo('review')"
              >
                Continuar <ng-icon name="heroArrowRight" size="14" />
              </button>
            </div>
          }
        </section>
      }

      <!-- ── Step 3: Review ── -->
      @if (step() === 'review') {
        <section appUiPanelCard class="card-panel">
          <div class="card-panel__head">
            <span appUiLabel class="mb-1 block uppercase">Passo 3</span>
            <h2 appUiPanelTitle class="mb-0 text-[1.2rem]">Revise antes de criar</h2>
            <p appUiPanelText>Confirme os dados e escolha como deseja registrar a venda.</p>
          </div>

          <div class="review-grid">
            <div appUiPanelCard class="review-section">
              <h3>Lead</h3>
              <p>{{ selectedContact()?.label }}</p>
            </div>
            <div appUiPanelCard class="review-section">
              <h3>Produto</h3>
              <p>{{ selectedProduct()?.name }}</p>
              <p class="subtitle">{{ billingLabel(selectedProduct()?.billing_interval ?? null) }}</p>
            </div>
            <div appUiPanelCard class="review-section">
              <h3>Quantidade × valor</h3>
              <p>{{ quantity() }} × {{ formatMoney(selectedProduct()?.amount ?? null, selectedProduct()?.currency ?? null) }}</p>
            </div>
            <div appUiPanelCard class="review-section review-section--total">
              <h3>Total</h3>
              <p class="total-value">{{ formatMoney(computedTotal(), selectedProduct()?.currency ?? null) }}</p>
            </div>
          </div>

          @if (submitError()) {
            <div appUiPanelCard class="error-banner" role="alert">{{ submitError() }}</div>
          }

          <div class="card-panel__actions card-panel__actions--double">
            <button appButton variant="ghost" type="button" (click)="goTo('product')" [disabled]="submitting()">
              <ng-icon name="heroArrowLeft" size="14" /> Voltar
            </button>
            <div class="actions-right">
              <button
                appButton
                variant="secondary"
                type="button"
                (click)="submit('draft')"
                [disabled]="submitting()"
              >
                Salvar como rascunho
              </button>
              <button
                appButton
                variant="primary"
                type="button"
                (click)="submit('active')"
                [disabled]="submitting()"
              >
                <ng-icon name="heroCheck" size="14" />
                {{ submitting() ? 'Criando…' : 'Criar e ativar' }}
              </button>
            </div>
          </div>
        </section>
      }
    </section>
  `,
  styles: [
    `
      .catalog-shell {
        display: flex;
        flex-direction: column;
        gap: 1.25rem;
        padding-block: 1.5rem 3rem;
        padding-inline: var(--shell-pad-x);
        min-height: 100%;
      }

      .card-panel {
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }

      .card-panel__actions {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 0.75rem;
        padding-top: 0.75rem;
        border-top: 1px solid var(--color-line);
      }

      .card-panel__actions--double {
        align-items: center;
      }

      .actions-right {
        display: flex;
        gap: 0.5rem;
      }

      .search-field {
        position: relative;
        display: block;
        max-width: 28rem;
      }

      .search-field ng-icon {
        position: absolute;
        left: 0.7rem;
        top: 50%;
        transform: translateY(-50%);
        color: var(--color-muted);
        pointer-events: none;
      }

      .search-field input {
        padding-left: 2.1rem;
      }

      .lead-list {
        list-style: none;
        padding: 0;
        margin: 0;
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(15rem, 1fr));
        gap: 0.4rem;
        max-height: 16rem;
        overflow-y: auto;
      }

      .lead-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        text-align: left;
      }

      .lead-item__name {
        font-size: 0.88rem;
        color: var(--color-ink);
        font-weight: 700;
      }

      .lead-item__status {
        text-transform: none;
      }

      .new-contact {
        display: block;
      }

      .new-contact > summary {
        cursor: pointer;
        font-size: 0.85rem;
        color: var(--color-accent-strong);
        font-weight: 700;
        list-style: none;
        display: inline-flex;
        align-items: center;
        gap: 0.4rem;
      }

      .new-contact[open] > summary {
        margin-bottom: 0.75rem;
      }

      .new-contact__grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
        gap: 0.75rem;
        margin-bottom: 0.5rem;
      }

      .form-field {
        display: flex;
        flex-direction: column;
        gap: 0.3rem;
      }

      .form-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(15rem, 1fr));
        gap: 0.75rem;
      }

      .product-snapshot {
        display: flex;
        flex-direction: column;
        gap: 0.4rem;
      }

      .snapshot-row {
        display: flex;
        justify-content: space-between;
        font-size: 0.82rem;
      }

      .snapshot-value {
        color: var(--color-ink);
        font-weight: 700;
      }

      .total-preview {
        margin: 0;
        font-family: var(--font-brand);
        font-size: 1.1rem;
        font-weight: 720;
        color: var(--color-accent-strong);
      }

      .review-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(15rem, 1fr));
        gap: 0.85rem;
      }

      .review-section {
        display: block;
      }

      .review-section h3 {
        margin: 0 0 0.4rem;
        font-size: 0.7rem;
        font-weight: 720;
        text-transform: uppercase;
        color: var(--color-muted);
      }

      .review-section p {
        margin: 0;
        font-size: 0.9rem;
        color: var(--color-ink);
      }

      .review-section .subtitle {
        margin-top: 0.2rem;
        font-size: 0.78rem;
        color: var(--color-muted);
      }

      .review-section--total {
        color: var(--color-accent-strong);
      }

      .total-value {
        font-family: var(--font-brand);
        font-size: 1.3rem;
        font-weight: 760;
        color: var(--color-accent-strong);
      }

      .empty-inline {
        display: flex;
        gap: 0.85rem;
        align-items: center;
        padding: 1rem;
        border: 1px dashed var(--color-line);
        border-radius: var(--radius-md);
      }

      .product-card__icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 2rem;
        height: 2rem;
        border-radius: var(--radius-sm);
        background: color-mix(in oklch, var(--color-accent-soft) 70%, transparent);
        color: var(--color-accent-strong);
      }

      .product-card__icon--new {
        background: color-mix(in oklch, var(--color-accent) 18%, transparent);
      }

      .product-card__price {
        margin: 0;
        font-family: var(--font-brand);
        font-size: 0.92rem;
        font-weight: 720;
        color: var(--color-accent-strong);
      }

      .product-card-new {
        display: grid;
        min-height: 100%;
        place-items: center;
        align-content: center;
        gap: 0.5rem;
        text-align: center;
      }

      .create-panel {
        display: flex;
        flex-direction: column;
        gap: 0.85rem;
      }

      .create-panel__head {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 1rem;
        padding-bottom: 0.6rem;
        border-bottom: 1px solid var(--color-line);
      }

      .create-panel__actions {
        display: flex;
        justify-content: flex-end;
        gap: 0.5rem;
      }

      .type-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(14rem, 1fr));
        gap: 0.5rem;
      }

      .create-form-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 0.75rem;
      }

      .form-field--span-2 {
        grid-column: span 2;
      }

      .required {
        color: var(--color-tomato);
      }

      .error-banner {
        color: var(--color-tomato);
        font-size: 0.85rem;
      }

      .picker-toolbar {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 0.5rem;
      }

      .picker-search {
        flex: 1;
        min-width: min(100%, 18rem);
        max-width: 22rem;
      }

      .picker-new {
        margin-left: auto;
      }

      .picker-pagination {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 0.5rem;
      }

      .picker-pagination__page {
        font-size: 0.78rem;
        color: var(--color-muted);
      }

      @media (max-width: 760px) {
        .card-panel__actions {
          flex-direction: column;
          align-items: stretch;
        }

        .picker-new {
          margin-left: 0;
        }
      }
    `,
  ],
})
export class SalesCriarPage {
  private readonly leadsApi = inject(LeadsApiService);
  private readonly revenueApi = inject(RevenueApiService);
  private readonly salesApi = inject(SalesApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly toastr = inject(ToastrService);

  readonly step = signal<WizardStep>('lead');
  readonly stepsMeta: ReadonlyArray<{ key: WizardStep; label: string }> = [
    { key: 'lead', label: 'Lead' },
    { key: 'product', label: 'Produto' },
    { key: 'review', label: 'Revisar' },
  ];

  // Lead step state
  readonly loadingLeads = signal(false);
  readonly contacts = signal<ContactSearchResult[]>([]);
  readonly search = signal('');
  readonly selectedContact = signal<SelectedContact | null>(null);
  readonly newContactOpen = signal(false);
  readonly newContact = signal<NewContactDraft>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });

  private searchDebounce: ReturnType<typeof setTimeout> | null = null;

  // Product step state
  readonly loadingProducts = signal(false);
  readonly products = signal<RevenueProduct[]>([]);
  readonly selectedProductId = signal<string | null>(null);
  // Keep the selected product object so it survives pagination/search (it may
  // leave the current page after the user moves on).
  readonly selectedProductData = signal<RevenueProduct | null>(null);
  readonly quantity = signal(1);

  // Product picker search/filter/pagination (server-side).
  readonly productSearch = signal('');
  readonly productType = signal<string | null>(null);
  readonly productPage = signal(1);
  readonly productPageSize = signal(12);
  readonly productTotalPages = signal(0);
  readonly productHasNext = signal(false);
  readonly productHasPrev = signal(false);
  private productSearchDebounce: ReturnType<typeof setTimeout> | null = null;

  // Inline product create state
  readonly productCreateMode = signal<'closed' | 'type' | 'form'>('closed');
  readonly productDraft = signal<ProductDraft>(emptyProductDraft());
  readonly productSubmitting = signal(false);
  readonly productSubmitError = signal<string | null>(null);

  readonly productTypes = PRODUCT_TYPES;
  readonly billingOptions = PRODUCT_BILLING_INTERVALS;
  readonly customUnitOptions = PRODUCT_CUSTOM_UNIT_OPTIONS;
  readonly productTypeIcon = productTypeIcon;
  readonly productTypeLabel = productTypeLabel;
  readonly formatProductPrice = formatProductPrice;

  // Submit state
  readonly submitting = signal(false);
  readonly submitError = signal<string | null>(null);

  readonly visibleContacts = computed(() => this.contacts());

  readonly selectedProduct = computed(() => this.selectedProductData());

  readonly selectedProductLabel = computed(() => {
    const p = this.selectedProduct();
    return p ? p.name : 'Selecionar produto';
  });

  readonly derivedType = computed<SaleType>(() => {
    const p = this.selectedProduct();
    if (!p || !p.billing_interval || p.billing_interval === 'one_time') return 'one_time';
    return 'recurring';
  });

  readonly computedTotal = computed(() => {
    const p = this.selectedProduct();
    if (!p || !p.amount) return null;
    const amount = Number(p.amount);
    if (!Number.isFinite(amount)) return null;
    return (amount * Math.max(1, this.quantity())).toFixed(2);
  });

  readonly canContinueProduct = computed(() => {
    const p = this.selectedProduct();
    return !!p && p.amount != null && p.currency != null && this.quantity() >= 1;
  });

  readonly canUseNewContact = computed(() => {
    const c = this.newContact();
    return Boolean((c.email?.trim() || c.phone?.trim()) && (c.firstName?.trim() || c.lastName?.trim() || c.email?.trim() || c.phone?.trim()));
  });

  constructor() {
    this.runContactSearch('');
    this.reloadProducts();
  }

  stepIndex(step: WizardStep): number {
    return this.stepsMeta.findIndex((s) => s.key === step);
  }

  goTo(target: WizardStep): void {
    this.step.set(target);
  }

  // ── Lead step ──
  onSearchChange(value: string): void {
    this.search.set(value);
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
    const term = value.trim();
    if (term.length > 0 && term.length < 2) {
      // Aguarda 2+ chars antes de chamar backend.
      this.loadingLeads.set(false);
      this.contacts.set([]);
      return;
    }
    this.searchDebounce = setTimeout(() => this.runContactSearch(term), 300);
  }

  private runContactSearch(term: string): void {
    this.loadingLeads.set(true);
    this.leadsApi
      .searchContacts(term, term ? 20 : 10)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.contacts.set(data);
          this.loadingLeads.set(false);
        },
        error: () => this.loadingLeads.set(false),
      });
  }

  contactName(c: ContactSearchResult): string {
    const full = `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim();
    return full || c.id.slice(0, 8);
  }

  contactStatusLabel(status: string): string {
    const map: Record<string, string> = {
      active: 'Ativo',
      merged: 'Mesclado',
      archived: 'Arquivado',
    };
    return map[status] ?? status;
  }

  contactStatusTone(status: string): BadgeTone {
    if (status === 'active') return 'success';
    if (status === 'merged') return 'warning';
    if (status === 'archived') return 'danger';
    return 'neutral';
  }

  pickExisting(c: ContactSearchResult): void {
    this.selectedContact.set({
      id: c.id,
      label: this.contactName(c),
      source: 'existing',
    });
  }

  toggleNewContact(): void {
    this.newContactOpen.update((v) => !v);
  }

  updateNewContact<K extends keyof NewContactDraft>(field: K, value: NewContactDraft[K]): void {
    this.newContact.update((c) => ({ ...c, [field]: value }));
  }

  useNewContact(): void {
    if (!this.canUseNewContact()) return;
    const draft = this.newContact();
    const fullName = `${draft.firstName} ${draft.lastName}`.trim();
    const label = fullName || draft.email || draft.phone || 'Novo contato';
    this.selectedContact.set({
      id: 'pending',
      label: `${label} (novo)`,
      source: 'new',
      draft: { ...draft },
    });
  }

  // ── Product step ──
  private reloadProducts(): void {
    this.loadingProducts.set(true);
    const term = this.productSearch().trim();
    const params: Parameters<RevenueApiService['listProducts']>[0] = {
      status: 'active',
      page: this.productPage(),
      pageSize: this.productPageSize(),
    };
    if (this.productType()) params.type = this.productType()!;
    if (term.length >= 2) params.search = term;
    this.revenueApi
      .listProducts(params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resp) => {
          this.products.set(resp.data as RevenueProduct[]);
          this.productTotalPages.set(resp.meta.totalPages);
          this.productHasNext.set(resp.meta.hasNextPage);
          this.productHasPrev.set(resp.meta.hasPreviousPage);
          this.loadingProducts.set(false);
        },
        error: () => this.loadingProducts.set(false),
      });
  }

  onProductSearchChange(value: string): void {
    this.productSearch.set(value);
    if (this.productSearchDebounce) clearTimeout(this.productSearchDebounce);
    const term = value.trim();
    if (term.length > 0 && term.length < 2) return;
    this.productSearchDebounce = setTimeout(() => {
      this.productPage.set(1);
      this.reloadProducts();
    }, 300);
  }

  setProductType(value: unknown): void {
    const v = value as string | null;
    this.productType.set(v === '__all__' ? null : v);
    this.productPage.set(1);
    this.reloadProducts();
  }

  prevProductPage(): void {
    if (!this.productHasPrev() || this.loadingProducts()) return;
    this.productPage.update((p) => Math.max(1, p - 1));
    this.reloadProducts();
  }

  nextProductPage(): void {
    if (!this.productHasNext() || this.loadingProducts()) return;
    this.productPage.update((p) => p + 1);
    this.reloadProducts();
  }

  setProduct(value: unknown): void {
    const id = value as string;
    const product = this.products().find((p) => p.id === id);
    if (!product || !this.isProductSellable(product)) return;
    this.selectedProductId.set(id);
    this.selectedProductData.set(product);
  }

  isProductSellable(p: RevenueProduct): boolean {
    return p.amount != null && p.currency != null;
  }

  setQuantity(value: unknown): void {
    const n = Number(value);
    if (!Number.isFinite(n) || n < 1) {
      this.quantity.set(1);
      return;
    }
    this.quantity.set(Math.floor(n));
  }

  billingLabel(interval: string | null | undefined): string {
    if (!interval) return '—';
    return BILLING_LABEL_PT_BR[interval] ?? interval;
  }

  customUnitLabel(unit: ProductCustomIntervalUnit | null | undefined): string {
    if (!unit) return 'Selecionar';
    return PRODUCT_CUSTOM_UNIT_OPTIONS.find((u) => u.value === unit)?.label ?? unit;
  }

  // ── Inline product create ──
  openProductCreate(): void {
    this.productDraft.set(emptyProductDraft());
    this.productSubmitError.set(null);
    this.productCreateMode.set('type');
  }

  closeProductCreate(): void {
    if (this.productSubmitting()) return;
    this.productCreateMode.set('closed');
  }

  selectProductType(typeKey: string): void {
    this.productDraft.update((d) => ({ ...d, type: typeKey }));
    this.productCreateMode.set('form');
  }

  backToTypeSelection(): void {
    this.productCreateMode.set('type');
  }

  updateProductDraft<K extends keyof ProductDraft>(field: K, value: ProductDraft[K]): void {
    this.productDraft.update((d) => {
      const next = { ...d, [field]: value };
      // Reset custom fields when billing changes away from custom.
      if (field === 'billingInterval' && value !== 'custom') {
        next.customValue = null;
        next.customUnit = null;
      }
      return next;
    });
  }

  canSubmitProduct(): boolean {
    const d = this.productDraft();
    if (!d.name.trim() || !d.amount.trim() || !d.currency) return false;
    if (d.billingInterval === 'custom' && (!d.customValue || !d.customUnit)) return false;
    return true;
  }

  submitProductCreate(): void {
    if (!this.canSubmitProduct() || this.productSubmitting()) return;
    const d = this.productDraft();
    this.productSubmitting.set(true);
    this.productSubmitError.set(null);

    const body: CreateRevenueProductRequest = {
      name: d.name.trim(),
      description: d.description.trim() || null,
      type: d.type,
      amount: d.amount.trim().replace(',', '.'),
      currency: d.currency,
      billing_interval: d.billingInterval,
      custom_interval_value: d.billingInterval === 'custom' ? d.customValue : null,
      custom_interval_unit: d.billingInterval === 'custom' ? d.customUnit : null,
      metadata: {},
    };

    this.revenueApi
      .createProduct(body)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (created) => {
          const product = created as RevenueProduct;
          this.products.update((arr) => [product, ...arr]);
          this.selectedProductId.set(product.id);
          this.selectedProductData.set(product);
          this.productCreateMode.set('closed');
          this.productSubmitting.set(false);
          this.toastr.success(`Produto "${product.name}" criado.`);
        },
        error: (err) => {
          this.productSubmitting.set(false);
          const message =
            (err?.error?.message as string | undefined) ?? 'Não foi possível criar o produto.';
          this.productSubmitError.set(message);
        },
      });
  }

  formatMoney(amount: string | null | undefined, currency: string | null | undefined): string {
    if (!amount || !currency) return '—';
    const value = Number(amount);
    if (!Number.isFinite(value)) return amount;
    try {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(value);
    } catch {
      return `${amount} ${currency}`;
    }
  }

  // ── Submit ──
  submit(action: 'draft' | 'active'): void {
    const product = this.selectedProduct();
    const contact = this.selectedContact();
    if (!product || !contact || !product.amount || !product.currency) {
      this.submitError.set('Dados incompletos.');
      return;
    }

    this.submitting.set(true);
    this.submitError.set(null);

    const resolveContactId$: Observable<string> =
      contact.source === 'new' && contact.draft
        ? this.leadsApi.createLead(contact.draft).pipe(switchMap((c) => of(c.id)))
        : of(contact.id);

    resolveContactId$
      .pipe(
        switchMap((contactId) => {
          const type = this.derivedType();
          const payload: CreateSalePayload = {
            contact_id: contactId,
            product_id: product.id,
            type,
            currency: product.currency!,
            status: action === 'draft' ? 'draft' : 'pending',
            line_items: [
              {
                product_id: product.id,
                quantity: this.quantity(),
                unit_amount: product.amount!,
              },
            ],
          };
          if (type === 'recurring') {
            payload.recurrence = this.buildRecurrenceFromProduct(product);
          }
          return this.salesApi.createSale(payload);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (created) => {
          this.submitting.set(false);
          this.toastr.success('Venda criada com sucesso.');
          const id = (created as { sale?: { id?: string } }).sale?.id;
          if (id && action === 'active') {
            this.salesApi.activateSale(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
              complete: () => this.router.navigate(['/vendas']),
            });
          } else {
            this.router.navigate(['/vendas']);
          }
        },
        error: (err) => {
          this.submitting.set(false);
          const message =
            (err?.error?.message as string | undefined) ??
            'Não foi possível criar a venda. Verifique os dados.';
          this.submitError.set(message);
          this.toastr.error('Falha ao criar venda.', message);
        },
      });
  }

  private buildRecurrenceFromProduct(product: RevenueProduct) {
    const billing = product.billing_interval as
      | 'monthly'
      | 'quarterly'
      | 'semiannual'
      | 'annual'
      | 'custom'
      | null
      | undefined;
    const nextCycleAt = this.computeNextCycle(billing, product.custom_interval_value, product.custom_interval_unit);
    return {
      billing_interval: billing!,
      custom_interval_value: product.custom_interval_value ?? null,
      custom_interval_unit: (product.custom_interval_unit as 'day' | 'week' | 'month' | null | undefined) ?? null,
      next_cycle_at: nextCycleAt.toISOString(),
      interval_count: 1,
    };
  }

  private computeNextCycle(
    billing: 'monthly' | 'quarterly' | 'semiannual' | 'annual' | 'custom' | null | undefined,
    customValue: number | null | undefined,
    customUnit: string | null | undefined,
  ): Date {
    const d = new Date();
    switch (billing) {
      case 'monthly':
        d.setMonth(d.getMonth() + 1);
        break;
      case 'quarterly':
        d.setMonth(d.getMonth() + 3);
        break;
      case 'semiannual':
        d.setMonth(d.getMonth() + 6);
        break;
      case 'annual':
        d.setFullYear(d.getFullYear() + 1);
        break;
      case 'custom': {
        const v = customValue ?? 1;
        if (customUnit === 'day') d.setDate(d.getDate() + v);
        else if (customUnit === 'week') d.setDate(d.getDate() + v * 7);
        else d.setMonth(d.getMonth() + v);
        break;
      }
      default:
        d.setMonth(d.getMonth() + 1);
    }
    return d;
  }
}
